import { premiumPaths } from "../../pages/Premium/navigation";
import "bootstrap-icons/font/bootstrap-icons.css";
import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { getChambers, getRoles } from "../../services/Dictionary";
import {
  addUser,
  assignChamber,
  assignRole,
  editUser,
  getAssignedRoles,
  getUser,
  removeRole,
} from "../../services/User";
import {
  validateEmail,
  validateLetters,
  validateLettersDigitsSpecChars,
} from "../../utils/Validators";
import InfoPopup from "./InfoPopup";
import ValidationInfo from "./ValidationInfo";
import { useAuth } from "../../context/AuthContext";
import LoadingSpinner from "./LoadingSpinner";

import logger from "../../utils/logger";

function UserData({
  userId,
  readOnly = false,
  handleBack,
  handleAfterSave,
  profileMode = false,
}) {
  const emptyUserId = "00000000-0000-0000-0000-000000000000";
  const { accessToken } = useAuth();
  const [email, setEmail] = useState("");
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [chamber, setChamber] = useState("");
  const [chambers, setChambers] = useState([]);
  const [userRoles, setUserRoles] = useState({});
  const [initialUserRoles, setInitialUserRoles] = useState({});
  const [roles, setRoles] = useState([]);
  const [isLoadingUser, setIsLoadingUser] = useState(userId !== emptyUserId);
  const [rolesLoading, setRolesLoading] = useState(true);
  const [rolesError, setRolesError] = useState("");
  const [saveWarningPopupTrigger, setSaveWarningPopupTrigger] = useState(0);
  const [saveWarningPopupTitle, setSaveWarningPopupTitle] = useState("");
  const [isSaving, setIsSaving] = useState(false);

  const [validators, setValidators] = useState({
    email: "false",
    firstName: "false",
    lastName: "false",
  });

  const validateUserData = () => {
    const nextValidators = {
      email: validateField("email", email),
      firstName: validateField("firstName", firstName),
      lastName: validateField("lastName", lastName),
    };
    setValidators(nextValidators);

    const invalidFields = [];
    if (nextValidators.email === "true") invalidFields.push("Adres email");
    if (nextValidators.firstName === "true") invalidFields.push("Imię");
    if (nextValidators.lastName === "true") invalidFields.push("Nazwisko");

    return invalidFields;
  };

  const showSaveWarning = (message) => {
    setSaveWarningPopupTitle(message);
    setSaveWarningPopupTrigger((current) => current + 1);
  };

  const getSaveErrorMessage = (error) =>
    error?.response?.data?.msg ||
    error?.message ||
    "Nie udało się zapisać danych użytkownika.";

  const handleSave = async () => {
    const invalidFields = validateUserData();
    if (invalidFields.length > 0) {
      showSaveWarning(
        `Uzupełnij wymagane dane użytkownika:\n${invalidFields.join("\n")}`,
      );
      return;
    }

    setIsSaving(true);
    try {
      let result = false;
      if (userId === emptyUserId) {
        result = await addUser(
          email,
          firstName,
          lastName,
          false,
          false,
          false,
          accessToken,
        );
      } else {
        result = await editUser(
          userId,
          email,
          firstName,
          lastName,
          accessToken,
        );
      }

      if (!result) {
        throw new Error("Nie udało się zapisać danych użytkownika.");
      }

      const chamberAssigned = await assignChamber(email, chamber, accessToken);
      if (!chamberAssigned) {
        throw new Error("Nie udało się przypisać użytkownika do izby.");
      }

      if (userRoles && Object.keys(userRoles).length > 0) {
        const changedRoles = Object.entries(userRoles).filter(
          ([roleId, isChecked]) =>
            Boolean(initialUserRoles[roleId]) !== Boolean(isChecked),
        );
        const rolePromises = changedRoles.map(([roleId, isChecked]) => {
          if (isChecked) {
            logger.log(`Assigning role ${roleId} to user ${email}`);
            return assignRole(email, roleId, accessToken);
          } else {
            logger.log(`Removing role ${roleId} from user ${email}`);
            return removeRole(email, roleId, accessToken);
          }
        });
        const roleResults = await Promise.all(rolePromises);
        if (roleResults.some((roleResult) => !roleResult)) {
          throw new Error("Nie udało się zapisać ról użytkownika.");
        }
        setInitialUserRoles({ ...userRoles });
      }
      handleAfterSave(
        userId === emptyUserId
          ? "Dodano użytkownika."
          : "Zapisano zmiany danych użytkownika.",
      );
    } catch (error) {
      logger.error("Błąd podczas zapisywania danych użytkownika:", error);
      showSaveWarning(getSaveErrorMessage(error));
    } finally {
      setIsSaving(false);
    }
  };

  const handleFieldInput = (e) => {
    let { name, value } = e.target;
    if (e.target.type === "checkbox") {
      value = e.target.checked;
      name = "roles";
    }

    switch (name) {
      case "email":
        if (validateLettersDigitsSpecChars(value, 254, true) || value === "")
          setEmail(value);
        break;
      case "firstName":
        if (validateLetters(value, 40, true) || value === "")
          setFirstName(value);
        break;
      case "lastName":
        if (validateLetters(value, 40, true) || value === "")
          setLastName(value);
        break;
      case "chamber":
        setChamber(value);
        break;
      case "roles":
        setUserRoles({ ...userRoles, [e.target.name]: value });
        break;
      default:
    }
  };

  const validateField = (name, value) => {
    logger.log("vF: " + name);
    switch (name) {
      case "email":
        return validateEmail(value, true) ? "false" : "true";
      case "firstName":
        return validateLetters(value, 40, true) ? "false" : "true";
      case "lastName":
        return validateLetters(value, 40, true) ? "false" : "true";
      default:
        return "true";
    }
  };

  const handleValidate = (e) => {
    const { name, value } = e.target;
    logger.log("UD - hV: " + name);
    setValidators((prevValidators) => ({
      ...prevValidators,
      [name]: validateField(name, value),
    }));
    logger.log(validators);
  };

  useEffect(() => {
    const fetchDictionaryData = async () => {
      setRolesLoading(true);
      setRolesError("");
      try {
        const chambersList = await getChambers(accessToken);
        setChambers(chambersList || []);

        const rolesList = await getRoles(accessToken);
        setRoles(Array.isArray(rolesList) ? rolesList : []);
      } catch (error) {
        logger.error("Błąd podczas pobierania danych słownikowych:", error);
        setRoles([]);
        setRolesError("Nie udało się pobrać listy ról.");
      } finally {
        setRolesLoading(false);
      }
    };

    fetchDictionaryData();
  }, [accessToken]);

  useEffect(() => {
    const fetchUserData = async () => {
      setIsLoadingUser(userId !== emptyUserId);
      try {
        if (userId !== emptyUserId) {
          logger.log("Tryb edycji");
          const user = await getUser(userId, accessToken);
          if (user) {
            setEmail(user.Email);
            setFirstName(user.FirstName);
            setLastName(user.LastName);
            setChamber(user.ChamberId);

            if (user.Email) {
              try {
                const assignedRoles = await getAssignedRoles(
                  user.Email,
                  accessToken,
                );
                if (assignedRoles) {
                  const rolesState = {};
                  assignedRoles.forEach((role) => {
                    rolesState[role.Id] = true;
                  });
                  setUserRoles(rolesState);
                  setInitialUserRoles(rolesState);
                }
              } catch (error) {
                logger.error("Błąd podczas pobierania ról użytkownika:", error);
              }
            }
          } else {
            logger.log("Tryb dodawania " + userId);
            setEmail("");
            setFirstName("");
            setLastName("");
          }
        } else {
          setEmail("");
          setFirstName("");
          setLastName("");
          setChamber("");
          setUserRoles({});
          setInitialUserRoles({});
        }
      } catch (error) {
        logger.error("Błąd podczas pobierania danych użytkownika:", error);
      } finally {
        setIsLoadingUser(false);
      }
    };

    fetchUserData();
  }, [userId, accessToken]);

  if (isLoadingUser) {
    return (
      <div className="d-flex flex-column align-items-center justify-content-center py-5">
        <LoadingSpinner text="Pobieranie danych użytkownika..." small={false} />
        <div className="text-muted mt-2">Trwa ładowanie danych.</div>
      </div>
    );
  }

  return (
    <div>
      <InfoPopup
        title={saveWarningPopupTitle}
        trigger={saveWarningPopupTrigger}
        warning={true}
      />
      {email || email === "" ? (
        <div className="d-flex flex-column">
          <div className="mb-1">
            <div className="d-flex align-items-center">
              <label className="form-label">Adres email *</label>
              <ValidationInfo
                title="Wypełnienie pola jest obowiązkowe. Maksymalna ilość znaków - 254"
                visible={validators.email}
              />
            </div>

            {profileMode ? (
              <input
                type="text"
                className={`form-control my-0 h2 rounded-3 ${
                  validators.email === "true" ? "is-invalid" : ""
                }`}
                maxLength="254"
                name="email"
                value={email}
                disabled
              />
            ) : (
              <input
                type="text"
                className="form-control my-0 h2 rounded-3"
                maxLength="254"
                name="email"
                value={email}
                onChange={handleFieldInput}
                onBlur={handleValidate}
                placeholder="Adres email użytkownika (np. imie_nazwisko@domena.com)"
                required={true}
                invalid={validators.email}
              />
            )}
          </div>

          <div className="d-flex flex-row mb-1">
            <div className="w23">
              <div className="d-flex align-items-center">
                <label className="form-label">Imię *</label>
                <ValidationInfo
                  title="Wypełnienie pola jest obowiązkowe. Maksymalna ilość znaków - 40"
                  visible={validators.firstName}
                />
              </div>

              <input
                type="text"
                className={`form-control my-0 h2 rounded-3 ${
                  validators.firstName === "true" ? "is-invalid" : ""
                }`}
                maxLength="40"
                name="firstName"
                value={firstName}
                onChange={handleFieldInput}
                onBlur={handleValidate}
                placeholder="Imię użytkownika"
                required={true}
                invalid={validators.firstName}
              />
            </div>

            <div className="ms-1 w23">
              <div className="d-flex align-items-center">
                <label className="form-label">Nazwisko *</label>
                <ValidationInfo
                  title="Wypełnienie pola jest obowiązkowe. Maksymalna ilość znaków - 40"
                  visible={validators.lastName}
                />
              </div>

              <input
                type="text"
                className={`form-control my-0 h2 rounded-3 ${
                  validators.lastName === "true" ? "is-invalid" : ""
                }`}
                maxLength="40"
                name="lastName"
                value={lastName}
                onChange={handleFieldInput}
                onBlur={handleValidate}
                placeholder="Nazwisko użytkownika"
                required={true}
                invalid={validators.lastName}
              />
            </div>
          </div>

          {!profileMode && (
            <div className="mb-1">
              <div className="d-flex flex-column">
                <div className="d-flex align-items-center">
                  <label className="form-label">Izba</label>
                </div>

                <select
                  className="form-control form-select my-0 h2 rounded-3"
                  name="chamber"
                  value={chamber}
                  onChange={handleFieldInput}
                  onBlur={handleValidate}
                  required="false"
                >
                  <option value="">Brak</option>
                  {chambers.map((c) => (
                    <option key={c.Id} value={c.Id}>
                      {c.Name} ({c.City})
                    </option>
                  ))}
                </select>
              </div>
            </div>
          )}

          {!profileMode && (
            <div className="border-top py-3">
              Dostęp do świadectw i słowników przydzielisz w{" "}
              <Link to={premiumPaths.admin}>Firmach Premium</Link>.
            </div>
          )}

          {!profileMode && (
            <div className="d-flex flex-column mb-2">
              <div className="d-flex align-items-center">
                <label className="form-label">Role</label>
              </div>

              <div>
                {rolesLoading ? (
                  <LoadingSpinner text="Pobieranie listy ról..." />
                ) : rolesError ? (
                  <div className="alert alert-warning py-2">{rolesError}</div>
                ) : roles.length > 0 ? (
                  roles.map((role, index) => (
                    <div key={index} className="mb-1">
                      <input
                        className="form-check-input"
                        type="checkbox"
                        id={role.Id}
                        name={role.Id}
                        checked={userRoles[role.Id] || false}
                        onChange={handleFieldInput}
                        title={role.Name}
                      />
                      <label className="form-check-label" htmlFor={role.Id}>
                        {role.Name}
                      </label>
                    </div>
                  ))
                ) : (
                  <div className="text-muted">Brak ról do wyświetlenia.</div>
                )}
              </div>
            </div>
          )}

          <div className="d-flex flex-column flex-wrap">
            <div className="d-flex flex-row w-100">
              <button
                className="btn btn-outline-primary h2 rounded-2 me-auto w7"
                onClick={handleBack}
              >
                Anuluj
              </button>
              <button
                type="button"
                className="btn btn-primary h2 rounded-2 w7"
                onClick={handleSave}
                disabled={isSaving}
              >
                {isSaving
                  ? "Zapisywanie..."
                  : userId === emptyUserId
                    ? "Dodaj"
                    : "Zapisz"}
              </button>
            </div>
          </div>
        </div>
      ) : (
        <div className="py-4">
          <LoadingSpinner text="Pobieranie danych..." small={false} />
        </div>
      )}
    </div>
  );
}

export default UserData;
